import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, InputGroup } from 'react-bootstrap';

export default function ManageColumns({ 
    tableName, 
    header, 
    index, 
    columns, 
    columnVisibility = {}, // Receive current visibility state from parent
    onColumnVisibilityChange,
    excludeColumns = [] // New prop to exclude specific columns
}) {
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [localColumnVisibility, setLocalColumnVisibility] = useState({});
    const [selectAll, setSelectAll] = useState(true);

    // Sync with parent's column visibility state
    useEffect(() => {
        if (columns && columns.length > 0) {
            const initialVisibility = {};
            columns.forEach((column, idx) => {
                // Use parent's visibility state if available, otherwise default to true
                initialVisibility[idx] = columnVisibility[idx] !== false;
            });
            setLocalColumnVisibility(initialVisibility);
            
            // Update select all state
            const allVisible = Object.values(initialVisibility).every(visible => visible);
            setSelectAll(allVisible);
        }
    }, [columns, columnVisibility]);

    const handleColumnToggle = (columnIndex) => {
        const newVisibility = { ...localColumnVisibility };
        newVisibility[columnIndex] = !newVisibility[columnIndex];
        setLocalColumnVisibility(newVisibility);
        
        // Update select all state
        const allVisible = Object.values(newVisibility).every(visible => visible);
        setSelectAll(allVisible);
        
        // Notify parent immediately
        onColumnVisibilityChange(newVisibility);
    };

    const handleSelectAllToggle = (checked) => {
        // Prevent hiding all columns
        // if (!checked) {
        //     alert("You must keep at least one column visible!");
        //     return;
        // }
        
        const newVisibility = {};
        columns.forEach((_, idx) => {
            newVisibility[idx] = checked;
        });
        setLocalColumnVisibility(newVisibility);
        setSelectAll(checked);
        
        // Notify parent immediately
        onColumnVisibilityChange(newVisibility);
    };

    const handleReset = () => {
        const defaultVisibility = {};
        columns.forEach((_, idx) => {
            defaultVisibility[idx] = true; // Reset all to visible
        });
        setLocalColumnVisibility(defaultVisibility);
        setSelectAll(true);
        
        // Notify parent immediately
        onColumnVisibilityChange(defaultVisibility);
    };

    // Filter out excluded columns and apply search filter
    const filteredColumns = columns.filter(column =>
        !excludeColumns.includes(column.header) && 
        column.header.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (!columns || columns.length === 0) {
        return null;
    }

    return (
        <>
            <div
                className="manage-columns-icon"
                style={{
                    cursor: 'pointer',
                    // padding: '2px 4px',
                    // borderRadius: '3px',
                    // transition: 'background-color 0.2s'
                }}
                // onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                // onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                onClick={() => setShowModal(true)}
                title="Manage Columns"
            >
                <i className="fa fa-ellipsis-v"></i>
            </div>

            {/* manage modal */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="md"
                backdrop={false}
                dialogClassName="modal-bottom-right"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '16px', fontWeight: 'bold' }}>
                        Manage Columns
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <InputGroup>
                            <InputGroup.Text>
                                <i className="fa fa-search"></i>
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Search columns..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </div>

                    <div className="mb-3 d-flex justify-content-between align-items-center">
                        <Form.Check
                            type="checkbox"
                            label="Show/Hide All"
                            checked={selectAll}
                            onChange={(e) => handleSelectAllToggle(e.target.checked)}
                        />
                        <small className="text-muted">
                            {Object.values(localColumnVisibility).filter(v => v).length} of {columns.length} visible
                        </small>
                    </div>

                    <div className="border rounded p-2" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {filteredColumns.length === 0 ? (
                            <div className="text-center text-muted py-3">
                                No columns found matching "{searchTerm}"
                            </div>
                        ) : (
                            filteredColumns.map((column) => {
                                // Find the actual column index in the original columns array
                                const actualIndex = columns.findIndex(col => col.header === column.header);
                                return (
                                    <div key={actualIndex} className="d-flex align-items-center mb-2">
                                        <Form.Check
                                            type="checkbox"
                                            id={`column-${actualIndex}`}
                                            checked={localColumnVisibility[actualIndex] || false}
                                            onChange={() => handleColumnToggle(actualIndex)}
                                            className="me-2"
                                        />
                                        <label
                                            htmlFor={`column-${actualIndex}`}
                                            className="mb-0 flex-grow-1"
                                            style={{ cursor: 'pointer' }}
                                        >
                                            {column.header}
                                        </label>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={handleReset} size="sm">
                        <i className="fa fa-undo me-2"></i>
                        Reset All
                    </Button>
                    <Button variant="primary" onClick={() => setShowModal(false)} size="sm">
                        <i className="fa fa-check me-2"></i>
                        Apply Changes
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}